import { useState } from 'react';
import { customerContactsApi } from '../../../services/api';
import { CustomerContact, CustomerContactListItem } from '../../../types/customer-contact';
import { showSuccess, showError } from '../../../utils/toast';
import { emptyContactForm } from '../utils/contactHelpers';

export const useContactPanel = (
  load: (page?: number) => Promise<void>,
  selectedCustomerFilter: number | '',
) => {
  const [selectedContact, setSelectedContact] = useState<CustomerContact | null>(null);
  const [viewMode, setViewMode] = useState<'view' | 'edit' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingContact, setLoadingContact] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyContactForm });

  const handleViewDetails = async (contact: CustomerContactListItem) => {
    try {
      setLoadingContact(true);
      setPanelError(null);
      const contactDetail = await customerContactsApi.getById(contact.id);
      setSelectedContact(contactDetail);
      setViewMode('view');
    } catch (e: any) {
      setPanelError(e?.response?.data?.message || e?.message || 'Failed to load contact details');
    } finally {
      setLoadingContact(false);
    }
  };

  const handleEdit = async (contact: CustomerContactListItem) => {
    try {
      setLoadingContact(true);
      setPanelError(null);
      const contactDetail = await customerContactsApi.getById(contact.id);
      setSelectedContact(contactDetail);
      setForm({
        customer_id: String(contactDetail.customer_id),
        name: contactDetail.name,
        email: contactDetail.email || '',
        phone: contactDetail.phone || '',
        title: contactDetail.title || '',
        street: contactDetail.street || '',
        city: contactDetail.city || '',
        state: contactDetail.state || '',
        zip: contactDetail.zip || '',
        country: contactDetail.country || '',
        notes: contactDetail.notes || '',
        is_primary: contactDetail.is_primary,
      });
      setViewMode('edit');
    } catch (e: any) {
      setPanelError(e?.response?.data?.message || e?.message || 'Failed to load contact details');
    } finally {
      setLoadingContact(false);
    }
  };

  const handleCreate = () => {
    setSelectedContact(null);
    setForm({
      ...emptyContactForm,
      customer_id: selectedCustomerFilter ? String(selectedCustomerFilter) : '',
    });
    setViewMode('edit');
  };

  const handleClosePanel = () => {
    setSelectedContact(null);
    setViewMode(null);
    setPanelError(null);
    setForm({ ...emptyContactForm });
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setPanelError(null);
    try {
      if (selectedContact) {
        await customerContactsApi.update(selectedContact.id, {
          ...form,
          customer_id: Number(form.customer_id),
        });
        showSuccess('Contact updated successfully');
      } else {
        await customerContactsApi.create({
          ...form,
          customer_id: Number(form.customer_id),
        });
        showSuccess('Contact created successfully');
      }
      await load();
      handleClosePanel();
    } catch (e: any) {
      const errorMsg = e?.response?.data?.message || e?.message || 'Failed to save contact';
      setPanelError(errorMsg);
      showError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (contactId: number) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;
    try {
      await customerContactsApi.delete(contactId);
      await load();
      showSuccess('Contact deleted successfully');
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Failed to delete contact');
    }
  };

  return {
    selectedContact,
    viewMode,
    submitting,
    loadingContact,
    panelError,
    form,
    handleViewDetails,
    handleEdit,
    handleCreate,
    handleClosePanel,
    handleFormChange,
    handleSubmit,
    handleDelete,
  };
};
